import React, { useCallback, useEffect, useState } from 'react'
import { Formik, Form } from 'formik'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { FiFileText, FiFile } from 'react-icons/fi'

import Yup from '~/services/yup'
import { api, authHeaders } from '~/services/api'
import ibge from '~/services/ibge'

import { downloadRelatorioJasper } from '~/util/file'

import {
    Button, Select, Calendar, Textbox
} from '~/components/Form'
import Spinner from '~/components/Spinner'

import { Container, ButtonContainer } from './styles'

import rangeDayGif from '~/assets/images/gifs/periodo-dias.gif'

const initialValues = {
    'plano-saude': '',
    juridico: '',
    cidade: '',
    instituicao: '',
    especialidade: '',
    'tipo-associado': '',
    'motivo-desligamento': '',
    'data-nascimento': '',
    'data-sindicalizacao': '',
    crm: '',
    aniversario: '',
    tipoPagamento: ''
}

const validation = Yup.object().shape({
    'plano-saude': Yup.string().ensure(),
    tipoPagamento: Yup.string().ensure(),
    juridico: Yup.string().ensure(),
    cidade: Yup.string().ensure(),
    instituicao: Yup.string().ensure(),
    especialidade: Yup.string().ensure(),
    'tipo-associado': Yup.string().ensure(),
    'motivo-desligamento': Yup.string().ensure(),
    'data-nascimento': Yup.string().nullable(),
    aniversario: Yup.string().nullable(),
    crm: Yup.string().nullable(),
    'data-sindicalizacao': Yup.string().nullable()
})

const nomeRelatorio = 'Relatório de Médicos'

export default function (props) {
    const [cidades, setCidades] = useState([])
    const [especialidades, setEspecialidades] = useState([])
    const [instituicoes, setInstituicoes] = useState([])
    const [tiposPagamentos, setTiposPagamentos] = useState([])
    const [motivoDesligamento, setMotivoDesligamento] = useState([])
    const [isSubmittingPlanilha, setIsSubmittingPlanilha] = useState(false)

    useEffect(() => {
        async function loadEspecialidades() {
            const response = await api.get('/especialidade')

            const serializado = response.map(especialidade => ({
                label: especialidade.descricao,
                value: especialidade.id
            }))

            setEspecialidades(serializado)
        }

        loadEspecialidades()
    }, [])

    useEffect(() => {
        async function loadCidades() {
            const { data } = await ibge.get('estados/PA/municipios')

            setCidades(data.map(({ nome }) => nome).sort())
        }

        loadCidades()
    }, [])

    useEffect(() => {
        async function loadInstituicoes() {
            const response = await api.get('instituicao')
            setInstituicoes(response)
        }

        loadInstituicoes()
    }, [])

    useEffect(() => {
        async function loadTiposPagamentos() {
            const response = await api.get('tipo_pagamento')

            setTiposPagamentos(response)
        }

        loadTiposPagamentos()
    }, [])

    useEffect(() => {
        async function loadMotivosDesligamento() {
            const response = await api.get('motivo_desligamento')

            setMotivoDesligamento(response)
        }

        loadMotivosDesligamento()
    }, [])

    const handleSubmit = useCallback(async (values, { resetForm }) => {
        const {
            cidade,
            'data-nascimento': nascimento,
            aniversario,
            'plano-saude': planoSaude,
            juridico,
            instituicao,
            especialidade,
            'tipo-associado': tipoAssociado,
            'motivo-desligamento': desligamento,
            crm,
            'data-sindicalizacao': sindicalizacao,
            tipoPagamento
        } = values

        const nascimentoInicio = nascimento ? nascimento[0] : null
        const nascimentoFim = nascimento ? nascimento[1] : null
        const aniversarioInicio = aniversario ? aniversario[0] : null
        const aniversarioFim = aniversario ? aniversario[1] : null
        const inicio = sindicalizacao ? sindicalizacao[0] : null
        const fim = sindicalizacao ? sindicalizacao[1] : null

        let filtros = ''

        if (tipoAssociado) {
            filtros += `Médicos: ${tipoAssociado.label}, `
        }

        if (nascimentoInicio) {
            filtros += `Data nasc.: ${format(new Date(nascimentoInicio), 'dd/MM/yyyy')} a ${nascimentoFim !== null ? format(new Date(nascimentoFim), 'dd/MM/yyyy') : format(new Date(nascimentoInicio), 'dd/MM/yyyy')}, `
        }

        if (aniversarioInicio) {
            filtros += `Aniversário: ${format(new Date(aniversarioInicio), 'dd/MM/yyyy')} a ${aniversarioFim !== null ? format(new Date(aniversarioFim), 'dd/MM/yyyy') : format(new Date(aniversarioInicio), 'dd/MM/yyyy')}, `
        }

        if (inicio) {
            filtros += `Data sindicalização: ${format(new Date(inicio), 'dd/MM/yyyy')} a ${fim !== null ? format(new Date(fim), 'dd/MM/yyyy') : format(new Date(inicio), 'dd/MM/yyyy')}, `
        }

        if (instituicao) {
            filtros += `Instituição: ${instituicao.label}, `
        }

        if (especialidade) {
            filtros += `Especialidade: ${especialidade.label}, `
        }

        if (cidade) {
            filtros += `Cidade: ${cidade.label}, `
        }

        if (planoSaude) {
            filtros += `${planoSaude.value === 1 ? 'Com plano de saúde' : 'Sem plano de Saúde'}, `
        }

        if (desligamento) {
            filtros += `Motivo de desligamento: ${desligamento.label}, `
        }

        if (juridico) {
            filtros += `${juridico.value === 1 ? 'Com causa no jurídico' : 'Sem causa no jurídico'}, `
        }

        if (tipoPagamento) {
            filtros += `Forma de pagamento: ${tipoPagamento.label}, `
        }

        if (crm) {
            filtros += `CRM: ${crm}, `
        }

        try {
            const response = await api.post('/relatorio', {
                relatorio: 'RelatorioAssociados',
                parametros: {
                    dataNascimentoInicio: nascimentoInicio !== null ? format(new Date(nascimentoInicio), 'yyyy-MM-dd') : '',
                    dataNascimentoFim: nascimentoFim !== null ? format(new Date(nascimentoFim), 'yyyy-MM-dd') : (nascimentoInicio !== null) ? format(new Date(nascimentoInicio), 'yyyy-MM-dd') : '',
                    dataSindicalizacaoInicio: inicio !== null ? format(new Date(inicio), 'yyyy-MM-dd') : '',
                    dataSindicalizacaoFim: fim !== null ? format(new Date(fim), 'yyyy-MM-dd') : (inicio !== null) ? format(new Date(inicio), 'yyyy-MM-dd') : '',
                    dataAniversarioInicio: aniversarioInicio !== null ? format(new Date(aniversarioInicio), 'yyyy-MM-dd') : '',
                    dataAniversarioFim: aniversarioFim !== null ? format(new Date(aniversarioFim), 'yyyy-MM-dd') : (aniversarioInicio !== null) ? format(new Date(aniversarioInicio), 'yyyy-MM-dd') : '',
                    instituicao_id: instituicao?.value || '',
                    especialidade: especialidade?.value || '',
                    cidade: cidade?.value || '',
                    planoSaude: planoSaude?.value || '',
                    socio: tipoAssociado?.value || '',
                    motivo_desligamento: desligamento?.value || '',
                    juridico: juridico?.value || '',
                    tipo_pagamento: tipoPagamento?.value || '',
                    crm: crm || '',
                    filtros: filtros !== '' ? filtros.slice(0, -2) : ''
                }
            }, {
                ...authHeaders(),
                responseType: 'blob'
            })

            downloadRelatorioJasper(response, nomeRelatorio)

            resetForm()
        } catch (e) {
            toast.error('Erro ao gerar o relatório.')
        }
    }, [])


    const handleSubmitPlanilha = useCallback(async (values, resetForm) => {
        setIsSubmittingPlanilha(true)

        const {
            cidade,
            'data-nascimento': nascimento,
            aniversario,
            'plano-saude': planoSaude,
            juridico,
            instituicao,
            especialidade,
            'tipo-associado': tipoAssociado,
            'motivo-desligamento': desligamento,
            crm,
            'data-sindicalizacao': sindicalizacao,
            tipoPagamento
        } = values

        const nascimentoInicio = nascimento ? nascimento[0] : null
        const nascimentoFim = nascimento ? nascimento[1] : null
        const aniversarioInicio = aniversario ? aniversario[0] : null
        const aniversarioFim = aniversario ? aniversario[1] : null
        const inicio = sindicalizacao ? sindicalizacao[0] : null
        const fim = sindicalizacao ? sindicalizacao[1] : null

        try {
            const response = await api.post('/relatorio/associados_excel', {
                relatorio: 'RelatorioAssociados',
                parametros: {
                    dataNascimentoInicio: nascimentoInicio !== null ? format(new Date(nascimentoInicio), 'yyyy-MM-dd') : null,
                    dataNascimentoFim: nascimentoFim !== null ? format(new Date(nascimentoFim), 'yyyy-MM-dd') : (nascimentoInicio !== null) ? format(new Date(nascimentoInicio), 'yyyy-MM-dd') : null,
                    dataAniversarioInicio: aniversarioInicio !== null ? format(new Date(aniversarioInicio), 'yyyy-MM-dd') : null,
                    dataAniversarioFim: aniversarioFim !== null ? format(new Date(aniversarioFim), 'yyyy-MM-dd') : (aniversarioInicio !== null) ? format(new Date(aniversarioInicio), 'yyyy-MM-dd') : null,
                    cidade: cidade?.value || null,
                    dataInicio: inicio !== null ? format(new Date(inicio), 'yyyy-MM-dd') : null,
                    dataFim: fim !== null ? format(new Date(fim), 'yyyy-MM-dd') : (inicio !== null) ? format(new Date(inicio), 'yyyy-MM-dd') : null,
                    planoSaude: planoSaude?.value || null,
                    juridico: juridico?.value || null,
                    instituicao: instituicao?.value || null,
                    especialidade: especialidade?.value || null,
                    tipo_associado: tipoAssociado?.value || null,
                    motivo_desligamento: desligamento?.value || null,
                    tipo_pagamento: tipoPagamento?.value || null,
                    crm: crm || null
                }
            }, {
                ...authHeaders(),
                responseType: 'blob'
            })

            if (response.type === 'application/json') {
                const fr = new FileReader()

                fr.onload = function (e) {
                    const json = e.target.result
                    toast.warn(JSON.parse(json).msg)
                }
                fr.readAsText(response)
            } else {
                downloadRelatorioJasper(response, nomeRelatorio, 'xlsx')
            }


            resetForm()
        } catch (e) {
            console.log(e)
            toast.error('Erro ao gerar o relatório.')
        }

        setIsSubmittingPlanilha(false)
    }, [])

    return (
        <Container className="animated fadeIn faster form" {...props}>
            <Formik
                initialValues={initialValues}
                validationSchema={validation}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({
                    isSubmitting, setFieldValue, resetForm, values
                }) => (
                    <Form>
                        <h2>{nomeRelatorio}</h2>

                        <Select
                            name="tipo-associado"
                            label="Tipo Associado"
                            onChange={(selected, meta) => setFieldValue(meta.name, selected)}
                            options={[
                                { label: 'Associado', value: 3 },
                                { label: 'Em associação', value: 2 },
                                { label: 'Aprovado', value: 4 },
                                { label: 'Ex-associado', value: 5 }
                            ]}
                            containerStyle={{ gridArea: 'f1' }}
                        />

                        <Select
                            name="motivo-desligamento"
                            label="Motivo desligamento"
                            onChange={(selected, meta) => setFieldValue(meta.name, selected)}
                            options={motivoDesligamento.map(m => ({
                                label: m.descricao,
                                value: m.id
                            }))}
                            containerStyle={{ gridArea: 'f2' }}
                        />

                        <Select
                            name="tipoPagamento"
                            label="Tipo de pagamento"
                            onChange={(selected, meta) => setFieldValue(meta.name, selected)}
                            options={tiposPagamentos.map(t => ({
                                label: t.descricao,
                                value: t.id
                            }))}
                            containerStyle={{ gridArea: 'f3' }}
                        />

                        <Calendar
                            name="data-sindicalizacao"
                            label="Data de Sindicalização"
                            maxDate={new Date()}
                            selectionMode="range"
                            containerStyle={{ gridArea: 'f4' }}
                            tooltip={`<p>Com o calendário aberto, selecione a primeira data,<br/>em seguida, a segunda.</p><br/><img src="${rangeDayGif}" style="width:300px; height:auto" />`}
                        />

                        <Select
                            name="instituicao"
                            label="Instituição"
                            onChange={(selected, meta) => setFieldValue(meta.name, selected)}
                            options={instituicoes.filter(insti => insti.desconto).map(instituicao => ({
                                label: `${instituicao.nome_fantasia} ${instituicao.sigla ? `(${instituicao.sigla})` : ''}`,
                                value: instituicao.id
                            }))}
                            containerStyle={{ gridArea: 'f5' }}
                        />

                        <Select
                            name="especialidade"
                            label="Especialidade"
                            onChange={(selected, meta) => setFieldValue(meta.name, selected)}
                            options={especialidades}
                            containerStyle={{ gridArea: 'f6' }}
                        />

                        <Select
                            name="cidade"
                            label="Cidade"
                            onChange={(selected, meta) => setFieldValue(meta.name, selected)}
                            options={cidades.map(cidade => ({
                                label: cidade,
                                value: cidade
                            }))}
                            containerStyle={{ gridArea: 'f7' }}
                        />

                        <Select
                            name="plano-saude"
                            label="Plano de Saúde?"
                            onChange={(selected, meta) => setFieldValue(meta.name, selected)}
                            options={[
                                { label: 'Com plano', value: 1 },
                                { label: 'Sem plano', value: 2 }

                            ]}
                            containerStyle={{ gridArea: 'f8' }}
                        />

                        <Select
                            name="juridico"
                            label="Causas no Jurídico"
                            onChange={(selected, meta) => setFieldValue(meta.name, selected)}
                            options={[
                                { label: 'Com causa', value: 1 },
                                { label: 'Sem causa', value: 2 }
                            ]}
                            containerStyle={{ gridArea: 'f9' }}
                        />

                        <Textbox
                            name="crm"
                            label="CRM"
                            containerStyle={{ gridArea: 'f10' }}
                        />

                        <Calendar
                            name="data-nascimento"
                            dateFormat="dd/mm/yy"
                            label="Data de Nascimento"
                            selectionMode="range"
                            maxDate={new Date(new Date().getFullYear() - 14, 11, 31)}
                            yearRange={`${new Date().getFullYear() - 100}:${new Date().getFullYear() - 14}`}
                            containerStyle={{ gridArea: 'f11' }}
                            tooltip={`<p>Com o calendário aberto, selecione a primeira data,<br/>em seguida, a segunda.</p><br/><img src="${rangeDayGif}" style="width:300px; height:auto" />`}
                        />

                        <Calendar
                            name="aniversario"
                            dateFormat="dd/mm"
                            label="Data de Aniversário"
                            yearNavigator={false}
                            containerClass="header-without-year"
                            selectionMode="range"
                            containerStyle={{ gridArea: 'f12' }}
                            tooltip={`<p>Com o calendário aberto, selecione a primeira data,<br/>em seguida, a segunda.</p><br/><img src="${rangeDayGif}" style="width:300px; height:auto" />`}
                        />

                        <Spinner
                            visible={isSubmitting}
                            label="Gerando relatório..."
                        />

                        <ButtonContainer>
                            <Button
                                onClick={resetForm}
                                className="transparent"
                            >
                                Limpar
                            </Button>

                            <Button
                                onClick={() => handleSubmitPlanilha(values, resetForm)}
                                loading={isSubmittingPlanilha}
                                className="white"
                            >
                                Gerar planilha
                                <FiFileText size={20} />
                            </Button>

                            <Button
                                type="submit"
                                loading={isSubmitting}
                                className="white"
                            >
                                Gerar relatório
                                <FiFile size={20} />
                            </Button>
                        </ButtonContainer>
                    </Form>
                )}
            </Formik>
        </Container>
    )
}
